import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { usePageMeta } from "@/utils/usePageMeta";

const EPOCHS = [
  {
    id: "stenaldern",
    era: "ca 4000 f.Kr.",
    title: "Stenålderns jägare",
    text: [
      "De första människorna kom hit för ungefär sex tusen år sedan. De var jägare och samlare som följde viltet och vattnet. De slog läger vid sjöarna, fiskade i älvarna och jagade älg i skogarna mellan Rossön och Backe.",
      "De lämnade efter sig mer än 120 dokumenterade boplatser – ett av de tätaste koncentrationerna av stenåldersboplatser i hela Ångermanland. Pilspetsar av kvarts och skiffer. Skrapor, eldstäder, kokgropar.",
      "Och de målade. Vid Brattforsberget, Brinnåsklippen och Nagasjö finns hällmålningar som är 6 000 år gamla. Älgar i rödockra, zigzag-mönster, processioner av figurer. Vid Mystiska Korset – ett tecken som ingen forskare har kunnat tyda.",
      "De valde den här platsen. Av alla platser i Norrland valde de Fjällsjöälven. Det säger något om marken.",
    ],
  },
  {
    id: "samerna",
    era: "Sedan urminnes tid",
    title: "Samerna – de första med renar",
    text: [
      "Långt innan socknar, bönder och finska nybyggare fanns samerna här. Området som idag heter Fjällsjö och Bodum tillhörde det som kallades Ångermanlands lappmark – ett vidsträckt land av skog, sjöar och fjäll där samer jagade, fiskade och bedrev renskötsel.",
      "Arkeologiska spår visar samisk närvaro i regionen sedan åtminstone vikingatiden (800–1000 e.Kr.). Stalotomter – lämningar av kåtor – har hittats i Frostviken längre västerut i det som idag är Strömsunds kommun. Den samiska markanvändningen sträckte sig genom Fjällsjö och Bodum, där skog och vatten gav gott om vilt och bete.",
      "Före 1220-talet tillhörde området det som kallas Finnmork – samiskt land som varken var svenskt, norskt eller ryskt. Det var samernas eget territorium, styrt av egna sedvanor. Först med den svenska statens expansion söderut började gränserna ritas om.",
      "På 1500-talet omnämns 'Lapparne uti Ångermanneland' i skattelängderna. Samerna betalade skatt genom en lappfogde och bedrev skogsrenskötsel – stationär och extensiv, anpassad efter den täta skogen snarare än fjällets vidder.",
      "Men statens kolonisering trängde undan samerna. Nybyggare fick skattefrihet för att odla mark som samerna använt i generationer. Lappskatteland avvecklades under 1700- och 1800-talen. De sista i Ångermanland försvann på 1880-talet. Många tidigare skattelappar blev nybyggare på sina egna gamla marker – men på statens villkor.",
      "Idag tillhör Strömsunds kommun det samiska förvaltningsområdet. Samebyar bedriver fortfarande renskötsel i regionen. I Strömsund finns ett samiskt rum på biblioteket. Samernas närvaro i dessa skogar är inte ett kapitel som tagit slut. Det är ett kapitel som pågår.",
    ],
  },
  {
    id: "medeltiden",
    era: "1400-talet",
    title: "Socknen bildas",
    text: [
      "Fjällsjö socken bildades under 1400-talet genom utbrytning ur Ramsele socken. Namnet Fjällsjö – från Fjällsjön – betydde inte fjäll i vår mening, utan obygd, vild skogsmark. Det var en kil av land mellan Lappland och Jämtland, ett gränsområde där få bodde men många passerade.",
      "År 1535 nämns socknen första gången i skriftliga källor. Gustav Vasa kräver skatt 1555 – och räknar 15 bönder. Hela befolkningen var kanske 120 personer. Det var en liten värld, men den var deras.",
    ],
  },
  {
    id: "finnarna",
    era: "Tidigt 1600-tal",
    title: "Finnarna kommer",
    text: [
      "I början av 1600-talet anlände finska nybyggare från Österbotten och Savolax. De lockades av kronan med upp till sex års skattefrihet för den som odlade upp ny mark i det som kallades Ångermanlands lappmark.",
      "De bildade finnbyar – i Fjällsjö, Hoting, Rudsjö, Rörström, Vängel och Tåsjö. Släktnamn som Lauinen och Hokkanen finns fortfarande i bygden. De röjde skog, brände sved, odlade och fiskade.",
      "Det blev inte alltid fridfullt. Konflikter med de som redan bodde här uppstod kring jakt och fiske. Drottning Kristina utfärdade lagar mot så kallade lösfinnar. Men de som stannade blev en del av bygden – och bygden blev en del av dem.",
    ],
  },
  {
    id: "bodum",
    era: "1799",
    title: "Bodum blir egen socken",
    text: [
      "År 1755 fick Fjällsjö sin egen präst – befolkningen hade vuxit till 325 personer. Tåsjö bröts ut som egen socken 1772. Och 1799 bildades Bodums socken genom utbrytning ur Fjällsjö, med kyrkan i byn Bodum. Rossön blev centralort.",
      "Det var byarna Bölen, Hällvattnet och de kringliggande gårdarna som begärt att få en egen socken. Avstånden till Backe var för långa, särskilt vintertid. Nu fick de sin egen kyrka, sin egen administration, sitt eget centrum.",
    ],
  },
  {
    id: "storhetstiden",
    era: "1850–1920",
    title: "Storhetstiden: Skogen, älven och framtiden",
    bloom: true,
    text: [
      "Det var skogen som förändrade allt.",
      "I mitten av 1800-talet nådde skogsbolagens expansion Ångermanland. Timret i skogarna kring Fjällsjö och Bodum var av högsta kvalitet – rakstammig tall, uppvuxen i kyla, långsamväxande och stark. Plötsligt var den värd pengar. Mycket pengar.",
      "Fjällsjöälven blev transportleden. Varje vår fälldes tusentals stockar i skogarna, kördes med häst till älvkanten och flottades nedströms mot sågverken vid kusten. Flottningen på Fjällsjöälven pågick ända till 1981 – en av de sista älvarna i Sverige.",
      "Befolkningen exploderade. Från 296 invånare år 1750 till 622 år 1850, till 2 231 år 1905. Backe förvandlades från en stilla kyrkby till ett livligt samhälle. Provinsialläkare 1861. Apotek. Sjukhem 1880, som blev lasarett 1883. Tingshus, landfiskal, handlare.",
      "Det byggdes vägar, skolor, bostäder. 1863 bildades Fjällsjö landskommun med Backe som centralort. Timmerkörarna behövde utrustning, hästarna behövde skor, folket behövde mat och kläder. Hela bygden levde av skogen.",
      "I Rossön startade Hans Oskar Mähler en smedja 1895 – först i Hoting, sedan i Rossön. Han reparerade jordbruksverktyg och tillverkade timmerslädar åt skogskörarna. Hans fyra söner gick in i företaget på 1940-talet och lade grunden för det som skulle bli AB Mählers & Söner – idag Nordens ledande tillverkare av snöplogar och väghållningsutrustning, fortfarande baserat i Rossön med 300 invånare.",
      "Järnvägen nådde bygden på 1920-talet och gav ännu bättre förutsättningar för timmertransport. Ett modernt sjukhus (länslasarett) byggdes i Backe 1964.",
      "Det var en tid då bygden växte, då folk flyttade hit, då det fanns arbete och framtidstro. Inte för att någon marknadsförde platsen – utan för att skogen fanns, älven rann, och folk behövde varandra.",
    ],
  },
  {
    id: "flottningen",
    era: "1880–1950",
    title: "Flottningens blomstringstid",
    bloom: true,
    text: [
      "Det var timret som gav bygden pengar. Men det var flottarna som gav bygden dess själ.",
      "Varje vår, så snart isen brast, började det. Tusentals stockar – fällda under vintern och upplagda i stora bråtar vid älvstränderna – sattes i rörelse nedströms. Fjällsjöälven blev på några veckor ett brusande, knakande, levande transportband av timmer. Stockar trängdes, stockar bröt upp, stockar fastnade i bakvatten och måste skiljas ut med händer, stänger och våta stövlar.",
      "Arbetet kallades flottning. Männen som gjorde det kallades flottare. De kom från bygden själv, från Rossön och Backe, från Vängel och Hoting, men också från Lappland och Österbotten. Varje vår samlades de – hundratals, ibland över tusen. De bodde i timrade flottarkojor utmed älven, sov i britsar i två eller tre våningar, åt köttsoppa och potatis ur samma gryta, sjöng på kvällarna och steg upp före gryningen.",
      "Lönen var dålig. Arbetet var farligt. Flera drunknade varje vår – förlorade fotfästet på en rullande stock, fastnade under en bråte, spolades bort i Nämforsen eller Sils kraftverk. Namn som Erik Andersson, Karl Magnusson, Olof Persson står på listor i kyrkobokföringen med samma dödsorsak: drunknad under flottning. I Fjällsjö och Bodum hittar man dem i arkivet. Unga karlar, ofta under 30.",
      "Men det var också ett yrke med stolthet. Att vara flottare krävde mod, balans och kännedom om älven. En skicklig pojke kunde springa tvärs över en vält av stockar utan att bli blöt. Flottarna hade sin egen språkdräkt – bråte, skilje, långkätta, stockbrott – och sina egna sånger. På söndagarna kom fruar och barn med korgar och det åts gemensamt på älvstranden.",
      "Bolagen som organiserade flottningen – bland andra Ångermanälvens Flottningsförening, bildad 1887 – blev mäktiga. De byggde flottningsrännor runt forsarna, sprängde grund i älvbottnen, röjde strandbråtar. Hela landskapet formades efter timrets behov.",
      "Järnvägen 1920-tal tog en del av arbetet. Men flottningen fortsatte – år efter år, decennium efter decennium. I mitten av 1950-talet var Fjällsjöälven fortfarande en av Sveriges viktigaste flottleder. Lastbilar och skogsmaskiner fanns inte ännu i den skalan.",
      "Det var först 1981 som den sista stocken flottades nerför Fjällsjöälven. Då hade dammar, kraftverk och lastbilsflottor gjort metoden gammaldags. När man slog in den sista spiken i den sista skiljen, var det en tidsepok som tog slut – tyst, utan ceremoni.",
      "Än idag hittar man i älvstränderna rester av flottningskojor, rostiga kedjor, trasiga stocksaxar och gamla märkeryxor i rottrassel. Vid Sils kraftverk utanför Backe finns en minnessten. Vid byservicekontoret i Backe kan man låna en bok med flottarminnen, nedtecknade av pensionärerna själva på 1970-talet innan de försvann.",
      "Om man går ner till Stenhammaren en tidig maj, står där en tystnad som inte finns någon annanstans. Det är älven som minns.",
    ],
  },
  {
    id: "foreningen",
    era: "1946–idag",
    title: "Marknaden, föreningen och framtiden",
    text: [
      "1946 arrangerades den första Fjällsjömarknaden. De tre första åren hölls den på Hembygdsgården, men från 1949 flyttade den till Stenhammaren – den plats vid Fjällsjöälven som kallas västras vackraste festplats. Marknaden har bara ställts in en enda gång, 1977, på grund av takproblem.",
      "1952 gick Bodums och Fjällsjö kommuner samman. 1974 blev bygden en del av Strömsunds kommun och flyttades administrativt från Västernorrlands län till Jämtlands län.",
      "Befolkningen sjönk. Backe hade sin topp 1965 med 1 022 invånare. Men det som inte sjönk var engagemanget. Rossöns IF har idag 420 medlemmar – i en by med 300 invånare. Backe IF arrangerar loppisar och Watercross. Bodums hembygdsförening håller Tunnbrödets dag vid liv.",
      "Mählers i Rossön exporterar snöplogar till hela Norden – och till Grönlands flygplatser. Fjällsjömarknaden fyller 80 år 2026 och firas med Sverigecupen i Watercross på Fjällsjöälven.",
      "Det är en bygd som krymper i antal men inte i kraft. Och den som kommer hit förstår varför folk stannar.",
    ],
  },
];

const SOURCES = [
  { text: "Fjällsjö socken – Wikipedia", url: "https://sv.wikipedia.org/wiki/Fj%C3%A4llsj%C3%B6_socken" },
  { text: "Bodums socken – Wikipedia", url: "https://sv.wikipedia.org/wiki/Bodums_socken" },
  { text: "Backe tätort – Wikipedia", url: "https://sv.wikipedia.org/wiki/Backe,_Str%C3%B6msunds_kommun" },
  { text: "Fjällsjö-finnarna – helenaburewijk.com", url: "https://helenaburewijk.com/2022/10/29/fjallsjo-finnarna/" },
  { text: "Ångermannasamerna – southsaamihistory.wordpress.com", url: "https://southsaamihistory.wordpress.com/2017/01/24/angermannasamerna-1500-1800-ca-del-i/" },
  { text: "Samiska kulturmiljöer i Frostviken – Strömsunds kommun", url: "https://www.stromsund.se/download/18.3993d9bc15a30c87acb7cd66/1487931426343/Samiska+kulturmilj%C3%B6er+i+Frostviken.pdf" },
  { text: "Samer i Västernorrland – Gaaltije", url: "https://gaaltije.se/onewebmedia/Samer%20i%20Vasternorrland.pdf" },
  { text: "Åsele lappmark – Wikipedia", url: "https://sv.wikipedia.org/wiki/%C3%85sele_lappmark" },
  { text: "Mählers 125 år – mahlers.se", url: "https://mahlers.se/en/2020/03/09/stronger-than-ever-mahlers-celebrates-125-years-of-success/" },
  { text: "Fjällsjömarknaden historik – backeif.se", url: "http://www.backeif.se/marknan_historik.htm" },
  { text: "Flottning i Sverige – Wikipedia", url: "https://sv.wikipedia.org/wiki/Flottning" },
  { text: "Ångermanälvens Flottningsförening – Wikipedia", url: "https://sv.wikipedia.org/wiki/%C3%85ngermanlands_flottningsf%C3%B6rening" },
  { text: "Flottningsminnen – Skogshistoriska Sällskapet", url: "https://www.skogshistoria.se" },
  { text: "Hällmålningar – backe.nu", url: "http://backe.nu/sv/kultur" },
  { text: "Rossöns Stenåldersmuseum – stromsund.se", url: "https://www.stromsund.se/3642.html" },
];

export default function KronikaSida() {
  usePageMeta({
    title: "Kronikan – vidalv.live",
    description: "Sex tusen år vid Fjällsjöälven – från stenålderns jägare till dagens Backe och Rossön. En kort lokalhistorisk krönika om bygden.",
    path: "/kronikan",
  });

  return (
    <div data-testid="kronika-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-1">Kronikan</p>
      <h1 className="font-fraktur-smooth text-2xl sm:text-3xl text-falu tracking-wide mb-3 leading-[1.05]">
        Sex tusen år vid älven
      </h1>
      <p className="text-base sm:text-lg text-bark/70 font-heading italic mb-4">
        Historien om Backe (Fjällsjö) och Rossön (Bodum)
      </p>
      <p className="text-sm text-bark leading-relaxed mb-12 max-w-2xl">
        Från stenålderns jägare som målade älgar på klipporna, via samernas renskötsel och de finska nybyggarna, till Mählers snöplogar som exporteras till Grönland. Det här är berättelsen om en bygd som alltid har burit mer än den visar.
      </p>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical timeline line */}
        <div className="absolute left-4 sm:left-6 top-0 bottom-0 w-px bg-warm-border" />

        {EPOCHS.map((epoch, i) => (
          <div
            key={epoch.id}
            data-testid={`epoch-${epoch.id}`}
            className={`relative pl-12 sm:pl-16 pb-12 ${i === EPOCHS.length - 1 ? "pb-0" : ""}`}
          >
            {/* Timeline dot */}
            <div
              className={`absolute left-2.5 sm:left-4.5 top-1 w-3 h-3 rounded-full border-2 ${
                epoch.bloom
                  ? "bg-falu border-falu"
                  : "bg-parchment border-warm-border"
              }`}
              style={{ left: epoch.bloom ? "0.55rem" : "0.55rem" }}
            />

            {/* Era badge */}
            <p className={`font-body text-xs font-semibold uppercase tracking-[0.15em] mb-1 ${
              epoch.bloom ? "text-falu" : "text-bark/50"
            }`}>
              {epoch.era}
            </p>

            {/* Title */}
            <h2 className={`font-fraktur-smooth text-xl sm:text-2xl tracking-wide mb-4 ${
              epoch.bloom ? "text-falu" : "text-ink"
            }`}>
              {epoch.title}
            </h2>

            {/* Bloom highlight box */}
            {epoch.bloom && (
              <div className="bg-falu/5 border-l-4 border-falu rounded-sm p-4 mb-4 -ml-2">
                <p className="text-xs font-semibold text-falu uppercase tracking-[0.15em]">
                  {epoch.id === "flottningen" ? "Flottarnas tid" : "Bygdens storhetstid"}
                </p>
                <p className="text-sm text-ink/70 mt-1">
                  {epoch.id === "flottningen"
                    ? "Varje vår sattes tusentals stockar i rörelse. Det var bygdens hjärtslag – i sjuttio år."
                    : "Skogen, timret och flottningen förvandlade Fjällsjö och Bodum från avlägsna byar till levande samhällen."}
                </p>
              </div>
            )}

            {/* Content paragraphs */}
            <div className="flex flex-col gap-4">
              {epoch.text.map((p, j) => (
                <p key={j} className={`text-sm leading-relaxed ${
                  epoch.bloom && j === 0 ? "text-ink font-heading text-lg italic" : "text-ink/80"
                }`}>
                  {p}
                </p>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Population table */}
      <div className="mt-12 bg-parchment border border-warm-border rounded-sm p-6">
        <h3 className="font-heading text-xl font-semibold text-ink mb-4">Befolkningsutveckling i Fjällsjö socken</h3>
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-3 text-center">
          {[
            { year: "1750", pop: "296" },
            { year: "1800", pop: "379" },
            { year: "1850", pop: "622" },
            { year: "1905", pop: "2 231", peak: true },
            { year: "1965", pop: "1 022" },
            { year: "2000", pop: "769" },
            { year: "2020", pop: "498" },
            { year: "2023", pop: "475" },
          ].map((d) => (
            <div key={d.year} className={`p-2 rounded-sm ${d.peak ? "bg-falu/10" : ""}`}>
              <p className={`font-body text-xs ${d.peak ? "text-falu font-semibold" : "text-bark/60"}`}>{d.year}</p>
              <p className={`font-heading text-lg font-bold ${d.peak ? "text-falu" : "text-ink"}`}>{d.pop}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-bark/50 mt-3">Källa: SCB, Wikipedia (Fjällsjö socken, Backe tätort). 2020 och 2023 avser Backe tätort enligt SCB.</p>
      </div>

      {/* Sources */}
      <div className="mt-10 pt-6 border-t border-warm-border">
        <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-3">Källor</p>
        <ul className="flex flex-col gap-2">
          {SOURCES.map((s, i) => (
            <li key={i}>
              <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-falu hover:text-falu-hover no-underline transition-colors">
                <ExternalLink size={11} /> {s.text}
              </a>
            </li>
          ))}
        </ul>
        <p className="text-xs text-bark/40 mt-4">Senast kontrollerat: 17 april 2026. Alla uppgifter är baserade på offentliga källor och kan verifieras via länkarna ovan.</p>
      </div>

      {/* Bygdens sång */}
      <div className="mt-10 bg-bone-alt/50 border border-warm-border rounded-sm p-6 sm:p-8 text-center">
        <p className="text-[11px] font-body font-semibold text-bark/55 uppercase tracking-[0.22em] mb-2">Bygdens hymn</p>
        <h3 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-2">Jämtlandssången</h3>
        <p className="text-sm text-bark mb-4 max-w-lg mx-auto">
          &bdquo;I trolska månskensnätter, när lunden söver in…&rdquo; &mdash; Peterson-Bergers hymn från 1895. Läs alla verser och lyssna på inledningsfrasen.
        </p>
        <Link to="/jamtlandssangen" data-testid="link-jamtlandssangen" className="inline-flex items-center gap-1.5 bg-falu hover:bg-falu-hover text-parchment font-body font-semibold text-sm px-4 py-2.5 rounded-sm transition-colors no-underline">
          Till hymnen →
        </Link>
      </div>

      {/* CTA */}
      <div className="mt-10 bg-pine rounded-sm p-6 sm:p-8">
        <h3 className="font-fraktur-smooth text-lg sm:text-xl text-parchment tracking-wide mb-2">Vet du mer?</h3>
        <p className="text-sm text-parchment/70 mb-4">Har du berättelser, fotografier eller minnen från bygdens historia? Vi vill gärna höra dem.</p>
        <a href="mailto:redaktion@vidalv.live?subject=Kronikan" className="inline-flex items-center bg-falu hover:bg-falu-hover text-parchment font-body font-semibold text-sm px-5 py-3 rounded-sm transition-colors no-underline">
          Dela din berättelse
        </a>
      </div>
    </div>
  );
}

